<template>
  <div>
    <section
      v-if="Object.keys(currentProcess).length"
      class="header-process-detail section"
    >
      <!-- Header -->
      <div class="level header-row mb-0">
        <div class="level-left">
          <b-button
            class="back-btn has-text-decoration-none"
            type="is-text"
            icon-left="arrow-left"
            title="Volver"
            @click="onBack"
          ></b-button>

          <div class="process-identity ml-3">
            <b-icon
              class="process-identity__status-icon"
              :icon="getStatusIcon(currentProcess.status)"
              :type="getStatusType(currentProcess.status)"
              size="is-medium"
            ></b-icon>

            <div class="process-identity__text">
              <h1 class="title is-5 truncate mb-0">
                {{ currentProcess.name || "-" }}
              </h1>
              <p class="subtitle is-7 truncate mb-0 has-text-grey">
                {{ currentProcess.idClient.nombre || "-" }}
              </p>
            </div>

            <b-dropdown
              v-if="hasPermission(tokenUser, ['admin'])"
              class="dropdown-status-processes status-dropdown"
              aria-role="list"
            >
              <template #trigger="{ active }">
                <button
                  type="button"
                  class="tag status-chip"
                  :class="getStatusType(currentProcess.status)"
                  v-tooltip.bottom="{
                    content: 'Haz clic para cambiar el estado del proceso',
                    classes: 'cultura-tooltip',
                  }"
                >
                  <span class="status-chip__label">
                    {{ getStatusLabel(currentProcess.status) }}
                  </span>
                  <b-icon
                    class="status-chip__caret"
                    :icon="active ? 'menu-up' : 'menu-down'"
                    size="is-small"
                  ></b-icon>
                </button>
              </template>

              <b-dropdown-item custom class="status-dropdown__heading">
                Cambiar estado del proceso
              </b-dropdown-item>
              <b-dropdown-item
                v-for="option in statusConfig"
                :key="option.value"
                :disabled="currentProcess.status === option.value"
                @click="changeStatus(option.value)"
              >
                <b-icon :icon="option.icon" :type="option.class"></b-icon>
                <span>{{ option.label }}</span>
                <b-tag
                  v-if="currentProcess.status === option.value"
                  type="is-light"
                  class="ml-auto has-text-black"
                >
                  Actual
                </b-tag>
              </b-dropdown-item>
            </b-dropdown>

            <span
              v-else
              class="tag status-chip status-chip--static"
              :class="getStatusType(currentProcess.status)"
            >
              <span class="status-chip__label">
                {{ getStatusLabel(currentProcess.status) }}
              </span>
            </span>
          </div>

          <span class="header-divider"></span>

          <div
            class="is-flex is-align-items-center has-text-grey"
            style="gap: 0.3rem"
          >
            <b-icon icon="calendar-blank-outline" size="is-small"></b-icon>
            <span>{{ formatDate(currentProcess.evaluationDate) }}</span>
          </div>

          <div class="is-flex is-align-items-center ml-4" style="gap: 0.5rem">
            <b-progress
              class="header-progress"
              :value="currentProcess.percentage || 0"
              size="is-small"
              type="is-success"
            ></b-progress>
            <span class="has-text-weight-semibold is-size-7">
              {{ (currentProcess.percentage || 0).toFixed(0) }}%
            </span>
          </div>
        </div>

        <div class="level-right">
          <img class="mytho-logo" src="@/assets/logo_Mytho.png" alt="Mytho" />
        </div>
      </div>
    </section>

    <!-- Tabs sit on top of the card -->
    <div class="tab-menu">
      <nav class="tabs is-boxed tabs-on-card">
        <ul>
          <li
            v-if="hasPermission(tokenUser, ['admin'])"
            :class="{ 'is-active': activeTab === 'configuration' }"
            style="margin-left: 50px;"
            @click="activeTab = 'configuration'"
          >
            <a href="javascript:void(0)">
              <b-icon icon="cog-outline" size="is-small"></b-icon>
              <span>Configuración</span>
            </a>
          </li>
          <li
            v-if="hasPermission(tokenUser, ['admin'])"
            :class="{ 'is-active': activeTab === 'collaborators' }"
            @click="activeTab = 'collaborators'"
          >
            <a href="javascript:void(0)">
              <b-icon icon="account-multiple-outline" size="is-small"></b-icon>
              <span>Colaboradores</span>
            </a>
          </li>
          <li
            v-if="hasPermission(tokenUser, ['admin', 'cultura'])"
            :class="{ 'is-active': activeTab === 'results' }"
            @click="activeTab = 'results'"
          >
            <a href="javascript:void(0)">
              <b-icon icon="chart-box-outline" size="is-small"></b-icon>
              <span>Resultados</span>
            </a>
          </li>
        </ul>
      </nav>

      <div class="tab-card">
        <Transition name="fade">
          <div v-if="activeTab === 'configuration'">
            <Configuration />
          </div>
        </Transition>
        <Transition name="fade">
          <div v-if="activeTab === 'collaborators'">
            <Collaborators />
          </div>
        </Transition>
        <Transition name="fade">
          <div v-if="activeTab === 'results'">
            <GuiaInterpretacion
              v-if="showResultsGuide"
              @continue="showResultsGuide = false"
            />
            <Results v-else />
          </div>
        </Transition>
      </div>
    </div>

    <b-dropdown
      class="is-hidden-desktop is-hidden-touch"
      v-model="activeTab"
      aria-role="list"
    >
      <template v-if="activeTab === 'collaborators'" #trigger>
        <b-button
          label="Colaboradores"
          icon-left="account-multiple-outline"
          icon-right="dots-horizontal"
        />
      </template>

      <template v-else-if="activeTab === 'results'" #trigger>
        <b-button
          label="Resultados"
          icon-left="chart-box-outline"
          icon-right="dots-horizontal"
        />
      </template>

      <template v-else-if="activeTab === 'configuration'" #trigger>
        <b-button
          label="Configuración"
          icon-left="cog-outline"
          icon-right="dots-horizontal"
        />
      </template>

      <b-dropdown-item value="collaborators" aria-role="listitem">
        <div class="media">
          <b-icon class="media-left" icon="account-multiple-outline"></b-icon>
          <div class="media-content">
            <h3>Colaboradores</h3>
          </div>
        </div>
      </b-dropdown-item>

      <b-dropdown-item value="results" aria-role="listitem">
        <div class="media">
          <b-icon class="media-left" icon="chart-box-outline"></b-icon>
          <div class="media-content">
            <h3>Resultados</h3>
          </div>
        </div>
      </b-dropdown-item>

      <b-dropdown-item value="results" aria-role="listitem">
        <div class="media">
          <b-icon class="media-left" icon="cog-outline"></b-icon>
          <div class="media-content">
            <h3>Configuración</h3>
          </div>
        </div>
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
import { mapState } from "vuex";
// Components
import Collaborators from "../sections/process/Collaborators.vue";
import Configuration from "../sections/process/Configuration.vue";
import Results from "../sections/results/Results.vue";
import GuiaInterpretacion from "../sections/results/GuiaInterpretacion.vue";

// Store
import store from "../store";

import {
  formatDate,
  getStatusIcon,
  getStatusLabel,
  getStatusType,
  hasPermission,
  statusConfig,
} from "../helpers";
import { updateProcess } from "../api";

export default {
  name: "ProcessDetail",
  data() {
    return {
      activeTab: "collaborators", // "overview",
      showResultsGuide: true,
      statusConfig,
    };
  },
  computed: {
    ...mapState(["isMovil", "tokenUser"]),
    currentProcess() {
      return store.state.selectedProcess || {};
    },
  },
  created() {
    if (!Object.keys(this.currentProcess).length) {
      this.$router.replace({ name: "cultura" });
    }

    if (this.tokenUser && !this.tokenUser.admin) {
      this.activeTab = "results";
    }
  },
  methods: {
    onBack() {
      this.$router.replace({ name: "cultura" });
    },
    changeStatus(newStatus) {
      updateProcess({
        _id: this.currentProcess._id,
        status: newStatus,
      })
        .then(() => {
          this.currentProcess.status = newStatus;
          this.$store.dispatch("notify", {
            message: "Estado actualizado correctamente.",
            type: "is-success",
          });
        })
        .catch((err) => {
          console.log(err);
          this.$store.dispatch("notify", {
            message: "Error al actualizar el estado.",
            type: "is-danger",
          });
        });
    },
    // Helpers
    formatDate,
    getStatusIcon,
    getStatusLabel,
    getStatusType,
    hasPermission,
  },
  components: {
    Collaborators,
    Configuration,
    Results,
    GuiaInterpretacion,
  },
};
</script>

<style scoped>
.process-detail {
  min-height: 100vh;
}

.header-process-detail {
  display: flex;
  align-items: center;
  min-height: 96px;
  padding: 0.75rem 3rem !important;
  background-color: #f5f5f5;
}

.header-row {
  width: 100%;
  gap: 1rem;
}

.back-btn {
  padding-left: 0;
  padding-right: 0.25rem;
}

/* Card that groups the process identity (name + client) with its status,
   so they read as one "this process, in this state" unit. */
.process-identity {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  padding: 0.5rem 0.65rem 0.5rem 1rem;
  background-color: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.process-identity__status-icon {
  flex-shrink: 0;
}

.process-identity__text {
  min-width: 0;
}

/* Status pill — uses the same flat .tag look as the rest of the app
   (Processes list / Collaborators), with a caret to signal it's clickable. */
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  height: auto;
  padding: 0.4em 0.85em;
  border: none;
  border-radius: 9999px;
  cursor: pointer;
  font-weight: 500;
  transition: filter 0.15s ease;
}

.status-chip:hover {
  filter: brightness(0.96);
}

.status-chip:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.12);
}

.status-chip__label {
  line-height: 1;
}

.status-chip__caret {
  margin-left: 0.05rem;
  opacity: 0.7;
}

/* Read-only variant shown to non-admins. */
.status-chip--static {
  cursor: default;
}

.status-chip--static:hover {
  filter: none;
}

.status-dropdown__heading {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #7a7a7a;
  font-weight: 600;
}

/* Tight title/subtitle stack (override Bulma's negative subtitle margin) */
.header-row .title.is-5 {
  margin-bottom: 0.15rem !important;
}

.header-row .subtitle {
  margin-top: 0 !important;
}

/* Compact inline progress bar */
.header-progress {
  width: 120px;
  margin-bottom: 0 !important;
}

.header-divider {
  flex: none;
  width: 1px;
  height: 34px;
  margin: 0 1.25rem;
  background-color: #dcdcdc;
}

.mytho-logo {
  height: 40px;
  width: auto;
  object-fit: contain;
}

.dropdown-status-processes .dropdown-item {
  padding-right: 1rem !important;
}

.dropdown-status-processes a {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.is-justify-flex-end {
  justify-content: flex-end;
}

.has-text-decoration-none {
  text-decoration: none !important;
}
</style>

<style>
.tab-menu {
  padding: 0 0 3rem;
}

/* Active tab is a white card floating on a light-grey strip */
.tab-menu .tabs-on-card {
  margin-bottom: 0 !important;
  padding: 0.5rem 0 0;
  background-color: #f5f5f5;
}

.tab-menu .tabs-on-card ul {
  border-bottom: none !important;
}

.tab-menu .tabs-on-card.is-boxed li a {
  border: 0px solid transparent !important;
  border-radius: 4px 4px 0 0 !important;
  background-color: transparent;
  color: #4a4a4a;
  margin-bottom: 0;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.tab-menu .tabs-on-card.is-boxed li:hover a {
  background-color: rgba(191, 191, 191, 0.475);
  border: solid 0 rgba(191, 191, 191, 0.475) !important;
  color: #522687 !important;
}

.tab-menu .tabs-on-card.is-boxed li.is-active a {
  background-color: #fff;
  border: solid 0 #fff !important;
  color: #522687 !important;
  font-weight: 600;
  box-shadow: #00000029 0px 14px 20px;
}

.tab-menu .tabs-on-card.is-boxed li a .icon {
  margin-right: 0.4em;
}

.tab-menu .tab-card {
  padding: 1.5rem 3rem 0;
}
</style>



// WEBPACK FOOTER //
// src/components/cultura/views/ProcessDetail.vue